import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';

import TextWithRichContent from 'components/TextWithRichContent';
import { useCommentsRoot } from 'components/pages/Offer/Comments/Root';

import { sendEvent } from 'lib/gtag';

import { useUser } from 'providers/UserProvider';

import Footer from '../Footer';

const CommentBlockedMessage = dynamic(() => import('./CommentBlockedMessage'), {
  ssr: false,
});

const CommentFixed = dynamic(() => import('./CommentFixed'), {
  ssr: false,
});

const CommentItemBody = ({
  commentFixed,
  commentId,
  commentLikes,
  commentStatusName,
  commentText,
  hasReply,
  isContentBlocked,
  isDisliked,
  isReply,
  isLiked,
  isShowingReplies,
  onShowCommentLinkClick,
  onToggleReplies,
}) => {
  const { gaEventCategory, addDislike, addLike, setActiveAnswerCommentId } =
    useCommentsRoot();
  const { isLoggedIn, runCallbackIfLoggedIn } = useUser();

  const onLikeClickHandler = () => {
    if (!isLoggedIn) {
      sendEvent({
        action: 'like_comment_attempt_unlogged',
        category: gaEventCategory,
      });
    }
    runCallbackIfLoggedIn(() => {
      sendEvent({
        action: isLiked ? 'remove_like_comment' : 'add_like_comment',
        category: gaEventCategory,
      });
      addLike(commentId);
    });
  };

  const onDislikeClickHandler = () => {
    if (!isLoggedIn) {
      sendEvent({
        action: 'dislike_comment_unlogged',
        category: gaEventCategory,
      });
    }
    runCallbackIfLoggedIn(() => {
      sendEvent({
        action: isDisliked ? 'remove_dislike_comment' : 'add_dislike_comment',
        category: gaEventCategory,
      });
      addDislike(commentId);
    });
  };

  const onReplyHandler = () => {
    if (!isLoggedIn) {
      sendEvent({ action: 'reply_unlogged', category: gaEventCategory });
    }

    setActiveAnswerCommentId((prevCommentId) => {
      if (prevCommentId !== commentId) {
        sendEvent({ action: 'reply', category: gaEventCategory });
      }
      return commentId;
    });
  };

  return (
    <>
      {commentFixed && !isContentBlocked ? (
        <CommentFixed commentText={commentText} />
      ) : (
        <>
          {isContentBlocked ? (
            <CommentBlockedMessage
              commentStatusName={commentStatusName}
              onShowCommentLinkClick={onShowCommentLinkClick}
            />
          ) : (
            <>
              <TextWithRichContent
                className="text-balance pr-6 text-sm text-neutral-low-200 dark:text-neutral-high-100 md:pr-24 md:text-base md:[&>img]:block"
                full
              >
                {commentText}
              </TextWithRichContent>
              <Footer
                commentId={commentId}
                commentLikes={commentLikes}
                hasReply={hasReply}
                isDisliked={isDisliked}
                isLiked={isLiked}
                isReply={isReply}
                isShowingReplies={isShowingReplies}
                onDislike={onDislikeClickHandler}
                onLike={onLikeClickHandler}
                onReply={onReplyHandler}
                onToggleReplies={onToggleReplies}
              />
            </>
          )}
        </>
      )}
    </>
  );
};

CommentItemBody.propTypes = {
  commentFixed: PropTypes.bool.isRequired,
  commentId: PropTypes.number.isRequired,
  commentLikes: PropTypes.number.isRequired,
  commentStatusName: PropTypes.string.isRequired,
  commentText: PropTypes.string.isRequired,
  hasReply: PropTypes.bool.isRequired,
  isContentBlocked: PropTypes.bool.isRequired,
  isDisliked: PropTypes.bool.isRequired,
  isEdited: PropTypes.bool.isRequired,
  isReply: PropTypes.bool.isRequired,
  isLiked: PropTypes.bool.isRequired,
  isShowingReplies: PropTypes.bool.isRequired,
  onShowCommentLinkClick: PropTypes.func.isRequired,
  onToggleReplies: PropTypes.func.isRequired,
};

export default CommentItemBody;
